import * as faIcons from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState, useEffect } from 'react';
import Lottie from 'react-lottie';
// Default import, NOT `import * as`. lottie-web MUTATES its animationData
// (completeData stamps `__complete` onto it), and under Vite a namespace
// import of JSON is a real ES module namespace object — non-extensible — so
// the mutation throws "Cannot add property __complete, object is not
// extensible" DURING the ErrorPage render. Since this is the error-boundary
// fallback, that throw re-enters the boundary and loops until the page dies:
// a contained crash on any route becomes a dead tab. webpack's fake namespace
// was a plain mutable object, which is why CRA never surfaced this. The
// default export is a plain object literal in both bundlers, so it stays
// mutable. (O3 in docs/features/vite-migration/.)
import errorLottie from 'assets/animations/react_crash_error_lottie.json';
import { sendCrashEmail } from 'modules/App/_rum';
import { cgToast } from 'modules/Common/ui/toast';
// The plain i18n instance, NOT useTranslation(). This component is the
// error-boundary fallback: a throw in here re-enters the boundary and loops
// until the tab dies (see the errorLottie comment above for the last time that
// happened). i18n.t() is a pure function call with no React context, no
// Suspense and no lazy loading, so it cannot introduce a new way to fail. The
// trade-off — this page does not re-render on a language switch — is
// irrelevant on a dead-end screen.
import i18n from 'i18n';

export default function ErrorPage({ error, resetErrorBoundary }) {
  const [reloading, setReloading] = useState(false);
  const [reported, setReported] = useState(false);

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: errorLottie,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  const refreshPage = () => {
    setReloading(true);
    setTimeout(() => {
      if (resetErrorBoundary) {
        resetErrorBoundary();
      } else {
        window.location.reload();
      }
    }, 1000);
  };

  const reportError = () => {
    // Manually trigger error reporting
    if (error) {
      sendCrashEmail(error, {
        source: 'user-reported',
        url: window.location.href,
      });
    } else {
      sendCrashEmail(new Error('Unknown error reported by user'), {
        source: 'user-reported',
        url: window.location.href,
      });
    }
    setReported(true);
    cgToast.info(i18n.t('errorPage.reportedTitle', 'Error reported'), {
      description: i18n.t(
        'errorPage.reportedBody',
        "We've been notified of the error. Please refresh the page.",
      ),
    });
  };

  // Log additional details to console for debugging
  useEffect(() => {
    if (error) {
      console.error('Error details:', error);
    }
  }, [error]);

  return (
    <>
      <div className="flex flex-col items-center mt-16">
        <Lottie
          options={defaultOptions}
          width={600}
          isStopped={false}
          isPaused={false}
        />
        <div className="font-bold text-6xl">{i18n.t('errorPage.oops', 'Oops')}</div>
        <p className="font-semibold text-xl">
          {i18n.t('errorPage.subtitle', 'Something went wrong. Please try again later.')}
        </p>

        {error && (
          <div className="mt-4 p-4 bg-gray-100 rounded-md max-w-lg text-left">
            <h4 className="font-bold">{i18n.t('errorPage.detailsHeading', 'Error Details:')}</h4>
            <p className="text-red-600">
              {error.message || i18n.t('errorPage.unknownError', 'Unknown error')}
            </p>
          </div>
        )}

        <div className="flex mt-6 space-x-4">
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-md border border-transparent px-3 py-1.5 text-base leading-normal text-center align-middle cursor-pointer select-none transition-colors duration-150 disabled:opacity-65 disabled:cursor-not-allowed bg-primaryColor text-white hover:bg-primaryColor-dark"
            onClick={refreshPage}
          >
            <FontAwesomeIcon
              icon={faIcons.faSync}
              color="white"
              className={`mr-2 ${reloading ? 'animate-spin' : ''}`}
            />
            {i18n.t('errorPage.reloadPage', 'Reload page')}
          </button>

          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-md border border-transparent px-3 py-1.5 text-base leading-normal text-center align-middle cursor-pointer select-none transition-colors duration-150 disabled:opacity-65 disabled:cursor-not-allowed bg-bsGray-600 text-white hover:bg-bsGray-700"
            onClick={reportError}
            disabled={reported}
          >
            <FontAwesomeIcon
              icon={faIcons.faExclamationTriangle}
              color="white"
              className="mr-2"
            />
            {reported
              ? i18n.t('errorPage.errorReported', 'Error Reported')
              : i18n.t('errorPage.reportThisError', 'Report this error')}
          </button>
        </div>
        {reported && (
          <div
            role="alert"
            className="mt-1 rounded-md border px-4 py-3 bg-primaryColor/10 text-primaryColor-dark border-primaryColor/30"
          >
            <p className="m-0">
              {i18n.t(
                'errorPage.reportedBody',
                "We've been notified of the error. Please refresh the page.",
              )}
            </p>
          </div>
        )}
      </div>
    </>
  );
}
